// GROUND-M0 NODE BYTES (pure; bake, client and server decode the SAME bytes
// into the SAME mesh: standing = drawn by construction). One node = one file
// groundm/L<k>/<tx>_<ty>.bin, brotli on the wire like every bake artifact.
//
// A node is PARTS, its mesh a pure function of them (meshOf):
//   S       Uint8Array(257^2): the RTIN's lattice vertices — the RTIN itself
//           is implicit (a triangle splits iff its hypotenuse midpoint is in
//           S: right-triangle bisection is determined by its vertex set)
//   region  Uint8Array(256^2): the cut-region cells — their RTIN half-cells
//           are dropped, the region triangles drawn instead
//   hS      Int32Array: the height (mm) of every S vertex, raster order
//   free    { x, z, y } Int32Array (mm; x / z node-local): the region's own
//           vertices (the cut's new vertices, and lattice vertices the pave
//           moved), in first-use order
//   rtri    Int32Array: the region triangles' corners — a lattice id (< 257^2)
//           or 257^2 + a free vertex
//   rlab    Uint8Array per region triangle: 8 | the pave label (borecut tlab)
// Every coordinate is a whole mm.
//
// Layout (little-endian): 'GM03', u8 level, u8 0, u16 hq, i32 tx, i32 ty, f32
// under, f32 over (the node's measured error against L0, m), i32 y0 (mm),
// u32 |S|, u32 nFree, u32 nRegionTris, then: S as a bitmask, region as a
// bitmask, hS in units of hq above y0 (hq: the heights' common step, mm —
// the bake's data quantum; 1 when none): the four node corners as zigzag
// deltas, then every other S vertex in RTIN descent order — first met as a
// hypotenuse midpoint — as zigzag u - floor(mean of that hypotenuse's ends),
// then seven streams, each a
// varint byte length + its bytes (kept apart: brotli models each alone):
//   cnt  per region cell (raster order): its triangle count
//   sym  per region corner: 0..3 the cell's lattice corners (i,j) (i+1,j)
//        (i,j+1) (i+1,j+1); a NEW free vertex: 4..7 at corner 0..3 (a
//        lattice vertex the cut moved), 8..11 on the cell edge z = Z0,
//        z = Z0 + cs, x = X0, x = X0 + cs (fe: the other coordinate), 12
//        anywhere (fxz); 13 another lattice vertex (esc); 14 an earlier
//        free vertex off the cell (esc); 15 + k the cell's k-th known free
//        vertex (every free vertex is known to the cells whose closed box
//        holds it, in creation order)
//   esc  per sym 13: its lattice id - the cell's corner (i,j); per sym 14:
//        the newest free vertex's id - its id; zigzag
//   fe   per sym 8..11: the coordinate along the edge - the cell's corner
//        (mm, 0..cs)
//   fxz  per sym 12: x, z (mm) - the cell's corner (i,j), zigzag
//   fy   per new free vertex: y (mm) - its prediction, zigzag: the mean of
//        the free corners its triangle has already given (the road surface
//        is flat locally), else the cell's bilinear height there (integer
//        weights: bit-exact anywhere)
//   lab  the region labels as (label, run)
// The region triangles come out grouped by cell (raster), the free vertices
// numbered in first use: parts round-trip up to that order.

export const MAGIC = 0x33304d47;   // 'GM03'
export const N = 256, G = 257, GG = G * G;
const Q = 1000;                     // mm per m

// ------------------------------------------------------------ the mesh
// parts (+ level) -> { x, z, y (Float64Array, m; node-local x / z), idx
// (Uint32Array), lab (Uint8Array: 0 RTIN ground, else 8 | pave label),
// latticeOf (Int32Array per vertex: lattice id, -1 free) }
export function meshOf(p) {
  const s = 1 << p.level, S = p.S, R = p.region;
  const nS = p.hS.length, nF = p.free ? p.free.x.length : 0, nR = p.rtri ? p.rtri.length / 3 : 0;
  // a conforming RTIN over V vertices holds < 2 V triangles
  const X = new Float64Array(nS + nF), Z = new Float64Array(nS + nF), Y = new Float64Array(nS + nF);
  const LAT = new Int32Array(nS + nF), idx = new Uint32Array(3 * (2 * nS + nR)), lab = new Uint8Array(2 * nS + nR);
  // lattice id -> its vertex (raster order of S = the order of hS)
  const vmap = new Int32Array(GG);
  for (let v = 0, k = 0; v < GG; v++) {
    if (!S[v]) { vmap[v] = -1; continue; }
    const i = v % G, j = (v - i) / G;
    X[k] = (s * i + 0.5) * 0.5; Z[k] = (s * j + 0.5) * 0.5; Y[k] = p.hS[k] / Q; LAT[k] = v;
    vmap[v] = k++;
  }
  let nt = 0;
  // a leaf is emitted (a, c, b): the RTIN recursion's (a, b, c) runs clockwise
  // seen from above (+y), and the ground is drawn front side only —
  // counter-clockwise like the ring lattice (lodmath quadIndex) and the region
  // triangles (borecut); check-groundm's `winding` row holds it
  const leaf = (ax, ay, bx, by, cx, cy, lvl) => {
    if (lvl === 16 && R) {
      const ci = ((ax + bx + cx) / 3) | 0, cj = ((ay + by + cy) / 3) | 0;
      if (R[cj * N + ci]) return;
    }
    idx[3 * nt] = vmap[ay * G + ax]; idx[3 * nt + 1] = vmap[cy * G + cx]; idx[3 * nt + 2] = vmap[by * G + bx];
    nt++;
  };
  const rec = (ax, ay, bx, by, cx, cy, lvl) => {
    if (lvl <= 15) {
      const mx = (ax + bx) >> 1, my = (ay + by) >> 1;
      if (S[my * G + mx]) {
        rec(cx, cy, ax, ay, mx, my, lvl + 1);
        rec(bx, by, cx, cy, mx, my, lvl + 1);
        return;
      }
    }
    leaf(ax, ay, bx, by, cx, cy, lvl);
  };
  rec(0, 0, N, N, 0, N, 0);
  rec(N, N, 0, 0, N, 0, 0);
  if (nR) {
    const F = p.free;
    for (let i = 0; i < nF; i++) { X[nS + i] = F.x[i] / Q; Z[nS + i] = F.z[i] / Q; Y[nS + i] = F.y[i] / Q; LAT[nS + i] = -1; }
    for (let t = 0; t < nR; t++) {
      for (let e = 0; e < 3; e++) { const c = p.rtri[3 * t + e]; idx[3 * nt + e] = c < GG ? vmap[c] : nS + (c - GG); }
      lab[nt++] = p.rlab[t];
    }
  }
  return { x: X, z: Z, y: Y, idx: idx.subarray(0, 3 * nt), lab: lab.subarray(0, nt), latticeOf: LAT };
}

// ------------------------------------------------------------ bytes
class Out {
  constructor(n) { this.b = new Uint8Array(n); this.n = 0; }
  need(k) {
    if (this.n + k <= this.b.length) return;
    const nb = new Uint8Array(Math.max(this.b.length * 2, this.n + k));
    nb.set(this.b.subarray(0, this.n)); this.b = nb;
  }
  u8(v) { this.need(1); this.b[this.n++] = v; }
  vu(v) {
    this.need(8);
    while (v >= 0x80) { this.b[this.n++] = (v % 0x80) | 0x80; v = Math.floor(v / 0x80); }
    this.b[this.n++] = v;
  }
  vs(v) { this.vu(v < 0 ? -2 * v - 1 : 2 * v); }
  bits(m) {
    const n = Math.ceil(m.length / 8);
    this.need(n);
    for (let i = 0; i < m.length; i++) if (m[i]) this.b[this.n + (i >> 3)] |= 1 << (i & 7);
    this.n += n;
  }
}

// the S vertices below the node corners in RTIN descent order, each with
// its hypotenuse ends: fn(m, a, b) once per vertex
const CORNERS = [0, N, N * G, N * G + N];
function descend(S, fn) {
  const seen = new Uint8Array(GG);
  for (const v of CORNERS) seen[v] = 1;
  const rec = (ax, ay, bx, by, cx, cy, lvl) => {
    if (lvl > 15) return;
    const mx = (ax + bx) >> 1, my = (ay + by) >> 1, m = my * G + mx;
    if (!S[m]) return;
    if (!seen[m]) { seen[m] = 1; fn(m, ay * G + ax, by * G + bx); }
    rec(cx, cy, ax, ay, mx, my, lvl + 1);
    rec(bx, by, cx, cy, mx, my, lvl + 1);
  };
  rec(0, 0, N, N, 0, N, 0);
  rec(N, N, 0, 0, N, 0, 0);
}
const half = (a, b) => Math.floor((a + b) / 2);
const gcd = (a, b) => { while (b) [a, b] = [b, a % b]; return a; };

// the cell's bilinear height (mm) at node-local (x, z) mm, its corner (i, j)
// at (X0, Z0), side cs mm, corner heights h00 h10 h01 h11 — integer weights
function cellPred(X0, Z0, cs, h00, h10, h01, h11, x, z) {
  const dx = Math.max(0, Math.min(cs, x - X0)), dz = Math.max(0, Math.min(cs, z - Z0));
  return Math.round((h00 * (cs - dx) * (cs - dz) + h10 * dx * (cs - dz) + h01 * (cs - dx) * dz + h11 * dx * dz) / (cs * cs));
}
const lmm = (s, i) => (s * i + 0.5) * 500;           // lattice i -> node-local mm
// every cell whose closed box holds (x, z) mm -> fn(cell)
function cellsAt(s, x, z, fn) {
  const cs = s * 500, o = 250;                        // lmm(s, 0) = 250
  const fi = Math.floor((x - o) / cs), fj = Math.floor((z - o) / cs);
  const ei = (x - o) % cs === 0, ej = (z - o) % cs === 0;
  for (let j = ej ? fj - 1 : fj; j <= fj; j++) {
    for (let i = ei ? fi - 1 : fi; i <= fi; i++) if (i >= 0 && j >= 0 && i < N && j < N) fn(j * N + i);
  }
}

// parts + { level, tx, ty, under, over } -> Uint8Array (parts.rcell: each
// region triangle's cell)
export function encodeNode(p) {
  let nS = 0;
  for (let v = 0; v < GG; v++) nS += p.S[v] ? 1 : 0;
  const nF = p.free ? p.free.x.length : 0, nR = p.rtri ? p.rtri.length / 3 : 0;
  let y0 = 0;
  if (nS) { y0 = Infinity; for (const h of p.hS) if (h < y0) y0 = h; }
  const o = new Out(64 + GG / 4 + nS * 2);
  const head = new DataView(new ArrayBuffer(40));
  let hq = 0;
  for (const h of p.hS) hq = gcd(hq, h - y0);
  if (!(hq >= 1 && hq <= 0xffff)) hq = 1;
  head.setUint32(0, MAGIC, true); head.setUint8(4, p.level); head.setUint16(6, hq, true);
  head.setInt32(8, p.tx, true); head.setInt32(12, p.ty, true);
  head.setFloat32(16, p.under, true); head.setFloat32(20, p.over, true);
  head.setInt32(24, y0, true); head.setUint32(28, nS, true); head.setUint32(32, nF, true); head.setUint32(36, nR, true);
  for (let i = 0; i < 40; i++) o.u8(head.getUint8(i));
  o.bits(p.S);
  const R = p.region || new Uint8Array(N * N);
  o.bits(R);
  const hAt = new Int32Array(GG), uAt = new Int32Array(GG);
  for (let v = 0, k = 0; v < GG; v++) if (p.S[v]) { hAt[v] = p.hS[k++]; uAt[v] = (hAt[v] - y0) / hq; }
  let q = 0;
  for (const v of CORNERS) { o.vs(uAt[v] - q); q = uAt[v]; }
  if (nS > 4) descend(p.S, (m, a, b) => o.vs(uAt[m] - half(uAt[a], uAt[b])));
  const st = { cnt: new Out(1024), sym: new Out(1024), esc: new Out(64), fe: new Out(1024), fxz: new Out(1024), fy: new Out(1024), lab: new Out(64) };
  if (nR) {
    // the triangles by cell (stable)
    const byCell = new Map();
    for (let t = 0; t < nR; t++) { const c = p.rcell[t]; let L = byCell.get(c); if (!L) byCell.set(c, (L = [])); L.push(t); }
    const s = 1 << p.level, cs = s * 500;
    const newId = new Int32Array(nF).fill(-1), known = new Map();
    let nextF = 0;
    const labs = [];
    for (let c = 0; c < N * N; c++) {
      if (!R[c]) continue;
      const L = byCell.get(c) || [];
      st.cnt.vu(L.length);
      const ci = c % N, cj = (c - ci) / N, c0 = cj * G + ci, X0 = lmm(s, ci), Z0 = lmm(s, cj);
      const cor = [c0, c0 + 1, c0 + G, c0 + G + 1];
      for (const t of L) {
        let py = 0, pn = 0;                      // the triangle's free corners so far
        for (let e = 0; e < 3; e++) {
          const code = p.rtri[3 * t + e];
          if (code >= GG && newId[code - GG] >= 0) { py += p.free.y[code - GG]; pn++; }
          if (code < GG) {
            const k = cor.indexOf(code);
            if (k >= 0) st.sym.vu(k); else { st.sym.vu(13); st.esc.vs(code - c0); }
            continue;
          }
          const f = code - GG;
          if (newId[f] >= 0) {
            const K = known.get(c), k = K ? K.indexOf(newId[f]) : -1;
            if (k >= 0) st.sym.vu(15 + k); else { st.sym.vu(14); st.esc.vs(nextF - 1 - newId[f]); }
            continue;
          }
          newId[f] = nextF++;
          const x = p.free.x[f], z = p.free.z[f], dx = x - X0, dz = z - Z0;
          const ex = dx === 0 ? 0 : dx === cs ? 1 : -1, ez = dz === 0 ? 0 : dz === cs ? 1 : -1;
          const inX = dx > 0 && dx < cs, inZ = dz > 0 && dz < cs;
          if (ex >= 0 && ez >= 0) st.sym.vu(4 + ex + 2 * ez);
          else if (ez >= 0 && inX) { st.sym.vu(8 + ez); st.fe.vu(dx); }
          else if (ex >= 0 && inZ) { st.sym.vu(10 + ex); st.fe.vu(dz); }
          else { st.sym.vu(12); st.fxz.vs(dx); st.fxz.vs(dz); }
          cellsAt(s, x, z, (cc) => { let K = known.get(cc); if (!K) known.set(cc, (K = [])); K.push(newId[f]); });
          const pred = pn ? Math.round(py / pn) : cellPred(X0, Z0, cs, hAt[cor[0]], hAt[cor[1]], hAt[cor[2]], hAt[cor[3]], x, z);
          st.fy.vs(p.free.y[f] - pred);
          py += p.free.y[f]; pn++;
        }
        labs.push(p.rlab[t]);
      }
    }
    if (labs.length !== nR) throw new Error(`groundm: ${nR - labs.length} region triangles outside the region`);
    for (let t = 0; t < nR;) {
      const L = labs[t];
      let r = 1;
      while (t + r < nR && labs[t + r] === L) r++;
      st.lab.vu(L); st.lab.vu(r); t += r;
    }
  }
  for (const k of ['cnt', 'sym', 'esc', 'fe', 'fxz', 'fy', 'lab']) {
    const b = st[k];
    o.vu(b.n); o.need(b.n); o.b.set(b.b.subarray(0, b.n), o.n); o.n += b.n;
  }
  return o.b.slice(0, o.n);
}

// bytes -> parts + { level, tx, ty, under, over }
export function decodeNode(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (dv.getUint32(0, true) !== MAGIC) throw new Error('groundm: not a GM03 node');
  const level = dv.getUint8(4), hq = dv.getUint16(6, true), tx = dv.getInt32(8, true), ty = dv.getInt32(12, true);
  const under = dv.getFloat32(16, true), over = dv.getFloat32(20, true);
  const y0 = dv.getInt32(24, true), nS = dv.getUint32(28, true), nF = dv.getUint32(32, true), nR = dv.getUint32(36, true);
  let n = 40;
  const vuAt = (r) => { let v = 0, m = 1, b; do { b = u8[r.n++]; v += (b & 0x7f) * m; m *= 0x80; } while (b & 0x80); return v; };
  const zz = (v) => (v % 2 ? -(v + 1) / 2 : v / 2);
  const bits = (len) => {
    const m = new Uint8Array(len);
    for (let i = 0; i < len; i++) m[i] = (u8[n + (i >> 3)] >> (i & 7)) & 1;
    n += Math.ceil(len / 8);
    return m;
  };
  const S = bits(GG), R = bits(N * N);
  const main = { n };
  const hAt = new Int32Array(GG), uAt = new Int32Array(GG);
  let q = 0;
  for (const v of CORNERS) { q += zz(vuAt(main)); uAt[v] = q; }
  if (nS > 4) descend(S, (m, a, b) => { uAt[m] = half(uAt[a], uAt[b]) + zz(vuAt(main)); });
  const hS = new Int32Array(nS);
  for (let v = 0, k = 0; v < GG; v++) if (S[v]) hS[k++] = hAt[v] = y0 + uAt[v] * hq;
  const st = {};
  for (const k of ['cnt', 'sym', 'esc', 'fe', 'fxz', 'fy', 'lab']) { const len = vuAt(main); st[k] = { n: main.n, end: main.n + len }; main.n += len; }
  if (main.n !== u8.length) throw new Error(`groundm: ${u8.length - main.n} trailing bytes`);
  const free = { x: new Int32Array(nF), z: new Int32Array(nF), y: new Int32Array(nF) };
  const rtri = new Int32Array(nR * 3), rcell = new Int32Array(nR), rlab = new Uint8Array(nR);
  if (nR) {
    const s = 1 << level, cs = s * 500, known = new Map();
    let t = 0, nextF = 0;
    for (let c = 0; c < N * N; c++) {
      if (!R[c]) continue;
      const nt = vuAt(st.cnt);
      const ci = c % N, cj = (c - ci) / N, c0 = cj * G + ci, X0 = lmm(s, ci), Z0 = lmm(s, cj);
      const cor = [c0, c0 + 1, c0 + G, c0 + G + 1];
      for (let u = 0; u < nt; u++, t++) {
        rcell[t] = c;
        let py = 0, pn = 0;
        for (let e = 0; e < 3; e++) {
          const y = vuAt(st.sym);
          if (y < 4) { rtri[3 * t + e] = cor[y]; continue; }
          if (y === 13) { rtri[3 * t + e] = c0 + zz(vuAt(st.esc)); continue; }
          if (y === 14) { const f = nextF - 1 - zz(vuAt(st.esc)); rtri[3 * t + e] = GG + f; py += free.y[f]; pn++; continue; }
          if (y >= 15) { const f = known.get(c)[y - 15]; rtri[3 * t + e] = GG + f; py += free.y[f]; pn++; continue; }
          let dx, dz;
          if (y < 8) { dx = (y - 4) & 1 ? cs : 0; dz = (y - 4) & 2 ? cs : 0; }
          else if (y < 10) { dx = vuAt(st.fe); dz = y === 9 ? cs : 0; }
          else if (y < 12) { dz = vuAt(st.fe); dx = y === 11 ? cs : 0; }
          else { dx = zz(vuAt(st.fxz)); dz = zz(vuAt(st.fxz)); }
          const f = nextF++, x = X0 + dx, z = Z0 + dz;
          free.x[f] = x; free.z[f] = z;
          cellsAt(s, x, z, (cc) => { let K = known.get(cc); if (!K) known.set(cc, (K = [])); K.push(f); });
          const pred = pn ? Math.round(py / pn) : cellPred(X0, Z0, cs, hAt[cor[0]], hAt[cor[1]], hAt[cor[2]], hAt[cor[3]], x, z);
          free.y[f] = pred + zz(vuAt(st.fy));
          py += free.y[f]; pn++;
          rtri[3 * t + e] = GG + f;
        }
      }
    }
    for (let i = 0; i < nR;) { const L = vuAt(st.lab), r = vuAt(st.lab); rlab.fill(L, i, i + r); i += r; }
  }
  return { level, tx, ty, under, over, S, region: R.some((v) => v) ? R : null, hS, free, rtri, rlab, rcell };
}
